/* hydrus-cart.js
 * Companion to sections/hydrus-cart.liquid.
 *
 * Document-level event delegation so handlers survive Section Rendering
 * API innerHTML swaps. Registered once on the document via a singleton
 * guard — multiple script loads (e.g., navigation, section re-render)
 * won't double-bind.
 *
 * Update flow:
 *   1. User action (qty change, remove, xsell add, discount apply/remove).
 *   2. POST to /cart/{change,update,add}.js with sections: "hydrus-cart".
 *   3. Swap the response's rendered HTML into the current section.
 *   4. Re-compute free-shipping bar + any other DOM state from the fresh markup.
 *
 * The whole section re-renders on every mutation. The top Liquid block
 * recomputes scenario flags, savings, free-shipping progress — the DOM
 * stays authoritative.
 */
(function () {
  if (window.HydrusCart) return;
  window.HydrusCart = true;

  const SECTION_ID = 'hydrus-cart';
  const DEBOUNCE_MS = 350;

  function getSection() {
    return document.querySelector('.hydrus-cart[data-section-id]');
  }

  function setBusy(busy) {
    const section = getSection();
    if (section) {
      section.setAttribute('data-hydrus-cart-updating', busy ? 'true' : 'false');
    }
  }

  async function fetchSection(sectionId) {
    const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, {
      headers: { 'Accept': 'text/html' },
    });
    if (!res.ok) throw new Error(`Section fetch failed: ${res.status}`);
    return res.text();
  }

  function swapSection(html) {
    const section = getSection();
    if (!section) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector('.hydrus-cart');
    if (!incoming) return;
    section.innerHTML = incoming.innerHTML;
    section.setAttribute('data-scenario', incoming.getAttribute('data-scenario') || '');
  }

  async function refreshSection() {
    try {
      const sectionEl = getSection();
      const sectionId = sectionEl && sectionEl.getAttribute('data-section-id');
      if (!sectionId) return;
      const html = await fetchSection(sectionId);
      swapSection(html);
      // Dispatch the canonical cart-mutation event so the cart-icon bubble
      // in the header gets refreshed (legacy app.js CartDrawer.refresh
      // listener re-fetches ?sections=cart-bubble and swaps the count) and
      // any other cart-state subscribers (shipping-protection reconciler,
      // future analytics) re-evaluate. Without this dispatch, customers on
      // /cart who removed items saw stale bubble counts until next nav.
      // See docs/cart-mutation-event-flow.md §Finding #2.
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'hydrus-cart-page' } }));
      // If cart went to empty via remove/qty=0, refresh the page so the
      // empty-state branch renders cleanly without the form wrapper.
      const fresh = getSection();
      if (fresh && fresh.querySelector('.hydrus-cart-empty')) {
        // Already swapped in the empty-state markup — no page reload needed.
      }
    } catch (err) {
      console.error('[HydrusCart] section refresh failed', err);
    } finally {
      setBusy(false);
    }
  }

  // ---------- Quantity (debounced per-key) ----------
  const qtyTimers = new Map();

  async function updateQuantity(key, quantity) {
    setBusy(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.warn('[HydrusCart] qty update rejected', err);
      }
    } catch (err) {
      console.error('[HydrusCart] qty update failed', err);
    }
    refreshSection();
  }

  function scheduleQtyUpdate(key, quantity) {
    if (qtyTimers.has(key)) clearTimeout(qtyTimers.get(key));
    qtyTimers.set(
      key,
      setTimeout(() => {
        qtyTimers.delete(key);
        updateQuantity(key, quantity);
      }, DEBOUNCE_MS)
    );
  }

  // ---------- Remove ----------
  async function removeItem(key) {
    setBusy(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      if (!res.ok) console.warn('[HydrusCart] remove rejected');
    } catch (err) {
      console.error('[HydrusCart] remove failed', err);
    }
    // If this was the last item, hit the cart URL directly to let Liquid
    // render the empty branch. Section fetch works too but a full nav is
    // cleaner for the transition.
    const itemsLeft = document.querySelectorAll('.hydrus-cart-item').length;
    if (itemsLeft <= 1) {
      window.location.href = '/cart';
      return;
    }
    refreshSection();
  }

  // ---------- Cross-sell add ----------
  async function addXsell(variantId, btn) {
    if (!variantId) return;
    if (btn) btn.setAttribute('aria-busy', 'true');
    setBusy(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.warn('[HydrusCart] xsell add rejected', err);
        if (btn) btn.setAttribute('aria-busy', 'false');
        setBusy(false);
        return;
      }
      if (btn) {
        btn.setAttribute('data-added', 'true');
        btn.textContent = 'Added';
      }
    } catch (err) {
      console.error('[HydrusCart] xsell add failed', err);
    }
    refreshSection();
  }

  // ---------- Discount codes ----------
  async function applyDiscount(code, feedbackEl, btn) {
    if (!code) return;
    if (btn) btn.setAttribute('aria-busy', 'true');
    setBusy(true);
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      const cart = await res.json().catch(() => ({}));
      const applied = (cart.cart_level_discount_applications || []).some(
        (d) => (d.title || '').toUpperCase() === code.toUpperCase()
      );
      if (applied) {
        if (feedbackEl) {
          feedbackEl.hidden = false;
          feedbackEl.setAttribute('data-state', 'success');
          feedbackEl.textContent = 'Discount applied.';
        }
      } else if (feedbackEl) {
        feedbackEl.hidden = false;
        feedbackEl.setAttribute('data-state', 'error');
        feedbackEl.textContent = 'That code isn\u2019t valid for this cart.';
      }
    } catch (err) {
      console.error('[HydrusCart] discount apply failed', err);
      if (feedbackEl) {
        feedbackEl.hidden = false;
        feedbackEl.setAttribute('data-state', 'error');
        feedbackEl.textContent = 'Something went wrong. Try again.';
      }
    }
    if (btn) btn.setAttribute('aria-busy', 'false');
    refreshSection();
  }

  async function removeDiscount(code) {
    setBusy(true);
    try {
      // Remove one code while preserving any others that are still applied.
      const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await cartRes.json().catch(() => ({}));
      const remaining = (cart.cart_level_discount_applications || [])
        .map((d) => d.title)
        .filter((t) => t && t.toUpperCase() !== code.toUpperCase());
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: remaining.join(',') }),
      });
    } catch (err) {
      console.error('[HydrusCart] discount remove failed', err);
    }
    refreshSection();
  }

  // ---------- Event delegation ----------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-hydrus-cart-action]');
    if (!btn) return;
    const action = btn.getAttribute('data-hydrus-cart-action');

    if (action === 'remove') {
      e.preventDefault();
      const key = btn.getAttribute('data-key');
      if (key) removeItem(key);
      return;
    }

    if (action === 'increment' || action === 'decrement') {
      e.preventDefault();
      const wrap = btn.closest('[data-hydrus-cart-qty]');
      if (!wrap) return;
      const input = wrap.querySelector('[data-hydrus-cart-input]');
      if (!input) return;
      const step = Number(input.step || 1);
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      let next = Number(input.value || 1) + (action === 'increment' ? step : -step);
      if (next < min) next = min;
      if (next > max) next = max;
      input.value = next;
      scheduleQtyUpdate(input.getAttribute('data-key'), next);
      return;
    }

    if (action === 'xsell-add') {
      e.preventDefault();
      addXsell(btn.getAttribute('data-variant-id'), btn);
      return;
    }

    if (action === 'apply-discount') {
      e.preventDefault();
      const wrap = btn.closest('[data-hydrus-cart-discount]');
      if (!wrap) return;
      const input = wrap.querySelector('[data-hydrus-cart-discount-input]');
      const feedback = wrap.querySelector('[data-hydrus-cart-discount-feedback]');
      const code = (input && input.value || '').trim();
      if (!code) return;
      applyDiscount(code, feedback, btn);
      return;
    }

    if (action === 'remove-discount') {
      e.preventDefault();
      const code = btn.getAttribute('data-code');
      if (code) removeDiscount(code);
      return;
    }
  });

  // Direct input typing into qty — also debounced.
  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-hydrus-cart-input]');
    if (!input) return;
    const key = input.getAttribute('data-key');
    const value = Number(input.value);
    if (!key || !Number.isFinite(value) || value < 0) return;
    scheduleQtyUpdate(key, value);
  });

  // ---------- External cart-mutation listener (Phase 3W fix-pass) ----------
  // The cart page must re-render when OTHER components mutate the cart.
  // Specifically: the shipping-protection toggle (assets/hydrus-shipping-
  // protection.js) writes a cart attribute + adds/removes the protection
  // variant via /cart/update.js, then dispatches cart:refresh. Before this
  // listener, only the drawer + cart-bubble re-rendered (via legacy app.js
  // CartDrawer.refresh) — the cart page summary stayed stale, so toggling
  // protection off left the Subtotal / Total at checkout showing the old
  // value with the removed protection line still factored in.
  //
  // Guard against echoing our own dispatches (refreshSection emits
  // cart:refresh with source='hydrus-cart-page' so other subscribers
  // re-evaluate; we ignore those to avoid an infinite re-render loop).
  document.addEventListener('cart:refresh', (e) => {
    const origin = e.detail && e.detail.source;
    if (origin === 'hydrus-cart-page') return;
    refreshSection();
  });

  // Enter on discount input applies the code.
  document.addEventListener('keydown', (e) => {
    const input = e.target.closest('[data-hydrus-cart-discount-input]');
    if (!input || e.key !== 'Enter') return;
    e.preventDefault();
    const wrap = input.closest('[data-hydrus-cart-discount]');
    const btn = wrap && wrap.querySelector('[data-hydrus-cart-action="apply-discount"]');
    const feedback = wrap && wrap.querySelector('[data-hydrus-cart-discount-feedback]');
    const code = input.value.trim();
    if (code) applyDiscount(code, feedback, btn);
  });
})();
